/* WJW Increment 35 — product image ZIP and membership prompts.
 * Uses the current product's original CDN image URLs rendered server-side by Shopify.
 * Does not expose extra catalog imagery, alter purchase/cart state, or use gallery thumbnails.
 */
(() => {
  'use strict';
  const SELECTORS = '#download-images, #upgrade-membership-link, #non-member-images-link';
  let downloading = false;
  const MEMBERSHIP_URL = '/pages/wjw-wholesale-jewelry-b2b-membership';
  const dataEl = document.querySelector('[data-wjw35-product-images]');
  const modal = document.getElementById('wjw35-image-modal');
  const titleEl = document.getElementById('wjw35-image-modal-title');
  const copyEl = document.getElementById('wjw35-image-modal-text');
  const goEl = document.getElementById('wjw35-image-modal-go');

  function setStatus(link, message, isError = false) {
    if (!link) return;
    let status = link.parentElement?.querySelector('.wjw35-image-status');
    if (!status) {
      status = document.createElement('span');
      status.className = 'wjw35-image-status';
      status.setAttribute('role', 'status');
      status.setAttribute('aria-live', 'polite');
      link.insertAdjacentElement('afterend', status);
    }
    status.hidden = !message;
    status.textContent = message || '';
    status.dataset.state = isError ? 'error' : 'normal';
  }

  function showAccessModal(link, upgrade) {
    const href = link.dataset.href || (upgrade ? '/apps/memberships' : MEMBERSHIP_URL);
    const baseOrigin = /^https?:/i.test(window.location.origin) ? window.location.origin : 'https://wholesalejewelrywebsite.com';
    const destination = new URL(href, baseOrigin);
    // Keep existing WJW destinations; don't let untrusted data attributes create an external redirect.
    if (destination.origin !== window.location.origin && destination.hostname !== 'wholesalejewelrywebsite.com') return;
    if (!modal || !titleEl || !copyEl || !goEl || typeof modal.showModal !== 'function') {
      window.location.assign(destination.href);
      return;
    }
    titleEl.textContent = upgrade ? 'Upgrade to Enterprise' : 'Unlock Enterprise Benefits';
    copyEl.textContent = upgrade
      ? 'High-resolution product image downloads are included with WJW Enterprise. Upgrade to unlock image access and qualifying U.S. free shipping.'
      : 'High-resolution product image downloads are reserved for Enterprise members. Explore membership benefits to unlock access.';
    goEl.textContent = upgrade ? 'Manage Membership' : 'View Memberships';
    goEl.href = destination.href;
    modal.showModal();
  }

  function cleanName(text) {
    return String(text || 'product').trim().replace(/[^a-zA-Z0-9._-]+/g, '-').replace(/^[-.]+|[-.]+$/g, '').slice(0, 85) || 'product';
  }

  function originalFilename(url, index, sku, mime) {
    const name = new URL(url).pathname.split('/').pop() || '';
    const ext = (name.match(/\.(jpe?g|png|webp|gif|avif|tiff?)$/i) || [])[1]
      || ({ 'image/jpeg': 'jpg', 'image/png': 'png', 'image/webp': 'webp', 'image/gif': 'gif', 'image/avif': 'avif' })[mime]
      || 'jpg';
    return `${sku}-${String(index + 1).padStart(2, '0')}.${ext.toLowerCase()}`;
  }

  async function downloadImages(link) {
    if (downloading) return;
    let data;
    try { data = JSON.parse(dataEl?.textContent || '{}'); }
    catch (_) { setStatus(link, 'Product image information is unavailable. Please contact support.', true); return; }
    const base = /^https?:/i.test(window.location.origin) ? window.location.origin : 'https://wholesalejewelrywebsite.com';
    // Shopify Liquid ImageDrop.src may return https://, protocol-relative //, or storefront-relative /cdn/ URLs.
    const urls = Array.isArray(data.images) ? data.images.map(raw => {
      if (typeof raw !== 'string') return null;
      try {
        const url = new URL(raw, base);
        return url.protocol === 'https:' ? url.href : null;
      } catch (_) { return null; }
    }).filter(Boolean) : [];
    if (!urls.length) { setStatus(link, 'No downloadable product images are available.', true); return; }
    if (typeof window.JSZip !== 'function') {
      setStatus(link, 'The ZIP download library could not load. Please refresh and try again.', true);
      return;
    }
    const sku = cleanName(data.sku);
    downloading = true;
    link.setAttribute('aria-busy', 'true');
    const zip = new window.JSZip();
    const folder = zip.folder(`${sku}-images`);
    let fileURL;
    try {
      // Fail the *whole* export on any unsuccessful fetch; never generate incomplete ZIPs.
      for (let i = 0; i < urls.length; i++) {
        const url = new URL(urls[i]);
        if (!/^(?:cdn\.shopify\.com|[a-z0-9-]+\.myshopify\.com|wholesalejewelrywebsite\.com)$/i.test(url.hostname)) {
          throw new Error('Unexpected image host.');
        }
        setStatus(link, `Preparing images ${i + 1} of ${urls.length}…`);
        const response = await fetch(url.href, { mode: 'cors', credentials: 'omit' });
        if (!response.ok) throw new Error('An image could not be retrieved.');
        const blob = await response.blob();
        if (!blob.size || (blob.type && !blob.type.startsWith('image/') && blob.type !== 'application/octet-stream')) {
          throw new Error('An invalid image was returned.');
        }
        folder.file(originalFilename(url.href, i, sku, blob.type), blob);
      }
      setStatus(link, `Compressing ${urls.length} images…`);
      const content = await zip.generateAsync({ type: 'blob', compression: 'STORE', streamFiles: true });
      fileURL = URL.createObjectURL(content);
      const a = document.createElement('a');
      a.href = fileURL;
      a.download = `${sku}-images.zip`;
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setStatus(link, `Prepared ${urls.length} full-size product image${urls.length === 1 ? '' : 's'} for download.`);
      setTimeout(() => { if (fileURL) URL.revokeObjectURL(fileURL); }, 60000);
    } catch (error) {
      console.error('WJW image ZIP failed:', error);
      setStatus(link, 'Unable to create the complete image ZIP. Please try again or contact WJW Support.', true);
    } finally {
      downloading = false;
      link.removeAttribute('aria-busy');
    }
  }

  document.addEventListener('click', event => {
    const dismiss = event.target.closest?.('[data-wjw35-close]');
    if (dismiss) { if (modal?.open) modal.close(); return; }
    const link = event.target.closest?.(SELECTORS);
    if (!link) return;
    event.preventDefault();
    if (link.id === 'download-images') { void downloadImages(link); return; }
    showAccessModal(link, link.id === 'upgrade-membership-link');
  });

  // Native <dialog> handles Escape. Close when clicking outside the card.
  modal?.addEventListener('click', event => {
    if (event.target === modal) modal.close();
  });
})();
